import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FilterDropdown, {FilterDropdownRef} from './FilterDropdown';
import FilterLayout from './FilterLayout';
import FilterRadioGroup, {RadioOption} from './FilterRadioGroup';
import LocationFilterDropdown from './LocationFilterDropdown';
import {HierarchyType} from '../../../admin/artisticobjects/types/OrganizationalUnit';
import './filters.scss';

interface LocationFilterProps {
  organizationalUnits?: string[];
  hierarchyType?: string;
  onFilterChange: (organizationalUnits?: string[], hierarchyType?: string) => void;
}

const LocationFilter: React.FC<LocationFilterProps> = ({
  organizationalUnits = [],
  hierarchyType = HierarchyType.CHURCH,
  onFilterChange
}) => {
  const {t} = useTranslation();
  const [tempOrganizationalUnits, setTempOrganizationalUnits] = useState<string[]>(organizationalUnits);
  const [tempHierarchyType, setTempHierarchyType] = useState<string>(hierarchyType);
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const hasActiveFilter = organizationalUnits.length > 0;

  const hierarchyOptions: RadioOption[] = [
    {label: t('gallery.filters.church'), value: HierarchyType.CHURCH},
    {label: t('gallery.filters.administrative'), value: HierarchyType.ADMINISTRATIVE}
  ];

  const handleHierarchyTypeChange = (newHierarchyType: string) => {
    setTempHierarchyType(newHierarchyType);
    // Clear organizational units when switching hierarchy type
    setTempOrganizationalUnits([]);
  };

  const applyFilter = () => {
    const filteredUnits = tempOrganizationalUnits.length > 0 ? tempOrganizationalUnits : undefined;
    onFilterChange(filteredUnits, tempHierarchyType);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyFilter();
      filterDropdownRef.current?.close();
    } else if (event.key === 'Escape') {
      // Reset temp values to current filter values and close dropdown
      setTempOrganizationalUnits(organizationalUnits);
      setTempHierarchyType(hierarchyType);
      filterDropdownRef.current?.close();
    }
  };

  const handleApplyClick = () => {
    applyFilter();
    filterDropdownRef.current?.close();
  };

  const clearFilter = () => {
    setTempOrganizationalUnits([]);
    setTempHierarchyType(HierarchyType.CHURCH);
    onFilterChange(undefined, HierarchyType.CHURCH);
    closeDropdown();
  };

  const closeDropdown = () => {
    filterDropdownRef.current?.close();
  };

  const getDisplayLabel = () => {
    const hierarchyLabel = t('gallery.filters.location');
    if (hasActiveFilter) {
      return `${hierarchyLabel} | ${organizationalUnits.length}`;
    }
    return hierarchyLabel;
  };

  return (
    <FilterDropdown
      ref={filterDropdownRef}
      label={getDisplayLabel()}
      hasActiveFilter={hasActiveFilter}
      onClearFilter={clearFilter}
    >
      <FilterLayout
        title={t('gallery.filters.filterByLocation')}
        onClear={clearFilter}
        onApply={handleApplyClick}
      >
        <FilterRadioGroup
          value={tempHierarchyType}
          options={hierarchyOptions}
          onChange={handleHierarchyTypeChange}
          name="hierarchyType"
          className="px-4 mb-3"
        />
        <LocationFilterDropdown
          hierarchyType={tempHierarchyType}
          value={tempOrganizationalUnits}
          onChange={setTempOrganizationalUnits}
          onKeyDown={handleKeyPress}
        />
      </FilterLayout>
    </FilterDropdown>
  );
};

export default LocationFilter;
